import { newId } from './crypto';
import { signDemoPayload } from './payload';
import { SCHEMA_VERSION } from './constants';
import type { DemoWebhookPayload, RunInput, ScenarioId } from './types';

export interface ScenarioDefinition {
  id: ScenarioId;
  title: string;
  description: string;
  requestCount: 1 | 2;
}

export const SCENARIOS: ScenarioDefinition[] = [
  {
    id: 'happy-path',
    title: 'Camino feliz',
    description: 'Un evento válido, firmado y a tiempo. Debe completarse sin advertencias.',
    requestCount: 1,
  },
  {
    id: 'duplicate',
    title: 'Duplicado',
    description:
      'El mismo evento se entrega dos veces (reintento normal del emisor). La segunda entrega debe detectarse como duplicado y no repetir el efecto.',
    requestCount: 2,
  },
  {
    id: 'invalid-signature',
    title: 'Firma inválida',
    description:
      'La firma HMAC no coincide con el payload. El evento debe rechazarse antes de cualquier efecto.',
    requestCount: 1,
  },
  {
    id: 'out-of-order',
    title: 'Desorden',
    description:
      'Dos eventos del mismo recurso llegan en orden inverso al de su secuencia. Debe quedar visible sin bloquear el procesamiento.',
    requestCount: 2,
  },
  {
    id: 'timeout',
    title: 'Timeout transitorio',
    description:
      'La dependencia falla en el primer intento por timeout. Debe reintentarse automáticamente y completarse.',
    requestCount: 1,
  },
];

export function getScenario(id: string): ScenarioDefinition | undefined {
  return SCENARIOS.find((s) => s.id === id);
}

interface BuildOptions {
  resourceId?: string;
  timestamp?: number;
  sequence?: number;
  idempotencyKey?: string;
  eventId?: string;
  data?: Record<string, unknown>;
  failureKind?: DemoWebhookPayload['failureKind'];
  corruptSignature?: boolean;
}

async function buildPayload(opts: BuildOptions): Promise<DemoWebhookPayload> {
  const timestamp = opts.timestamp ?? Math.floor(Date.now() / 1000);
  const data = opts.data ?? { amount: 4200, currency: 'EUR', customerId: 'cus_demo_1' };
  const signature = await signDemoPayload(timestamp, data);
  const payload: DemoWebhookPayload = {
    eventId: opts.eventId ?? newId('evt'),
    resourceId: opts.resourceId ?? newId('res'),
    sequence: opts.sequence ?? 1,
    occurredAt: new Date(timestamp * 1000).toISOString(),
    idempotencyKey: opts.idempotencyKey ?? newId('idem'),
    timestamp,
    signature: opts.corruptSignature ? `${signature.slice(0, -4)}dead` : signature,
    data,
  };
  if (opts.failureKind) payload.failureKind = opts.failureKind;
  return payload;
}

function toInput(scenarioId: ScenarioId, payload: DemoWebhookPayload): RunInput {
  return {
    schemaVersion: SCHEMA_VERSION,
    scenarioId,
    payload: payload as unknown as Record<string, unknown>,
    options: { deterministic: true },
  };
}

export interface ScenarioStep {
  input: RunInput;
  label: string;
}

/** Builds the one or two requests that demonstrate a given failure scenario. */
export async function buildScenarioSteps(scenarioId: ScenarioId): Promise<ScenarioStep[]> {
  switch (scenarioId) {
    case 'happy-path': {
      const payload = await buildPayload({});
      return [{ input: toInput(scenarioId, payload), label: 'Entrega única' }];
    }
    case 'invalid-signature': {
      const payload = await buildPayload({ corruptSignature: true });
      return [{ input: toInput(scenarioId, payload), label: 'Entrega con firma corrupta' }];
    }
    case 'timeout': {
      const payload = await buildPayload({ failureKind: 'transient' });
      return [{ input: toInput(scenarioId, payload), label: 'Entrega con dependencia lenta' }];
    }
    case 'duplicate': {
      const resourceId = newId('res');
      const eventId = newId('evt');
      const idempotencyKey = newId('idem');
      const timestamp = Math.floor(Date.now() / 1000);
      const data = { amount: 1999, currency: 'EUR', customerId: 'cus_demo_2' };
      const payload = await buildPayload({
        resourceId,
        eventId,
        idempotencyKey,
        timestamp,
        data,
        sequence: 1,
      });
      return [
        { input: toInput(scenarioId, payload), label: 'Primera entrega' },
        { input: toInput(scenarioId, { ...payload }), label: 'Reenvío duplicado del emisor' },
      ];
    }
    case 'out-of-order': {
      const resourceId = newId('res');
      const baseTimestamp = Math.floor(Date.now() / 1000);
      const first = await buildPayload({
        resourceId,
        sequence: 2,
        timestamp: baseTimestamp,
        data: { amount: 500, currency: 'EUR', customerId: 'cus_demo_3', step: 'confirmed' },
      });
      const second = await buildPayload({
        resourceId,
        sequence: 1,
        timestamp: baseTimestamp - 2,
        data: { amount: 500, currency: 'EUR', customerId: 'cus_demo_3', step: 'created' },
      });
      return [
        { input: toInput(scenarioId, first), label: 'Llega primero: secuencia 2' },
        { input: toInput(scenarioId, second), label: 'Llega después: secuencia 1 (desorden)' },
      ];
    }
  }
}
